import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';
import { Message } from '../Message';
import { ChatAsset } from '../ChatAsset';
import { StepsTracker } from '../StepsTracker';
import { ChildQuestionsToggle } from '../ChildQuestionsToggle';
import { ChatCannedAnswer } from '../ChatCannedAnswer';
import { NuxModal, Text, Icon, NewButton } from '../../../../../components';
import { usePortal } from '../../../../../providers';

import { questionShape, sourceShape, userShape } from '../../../../../propsShape';

const ChatItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  opacity: ${({ $opacity }) => ($opacity ? 0.1 : 1)};
`;

const ChatItemInnerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  border-radius: 8px;
  backdrop-filter: blur(200px);
  box-sizing: border-box;

  .mentionTrigger {
    display: none;
  }

  .mentionValue {
    display: inline-flex;
    border-radius: 4px;
    line-height: 20px;
    padding: 0 4px;
    cursor: pointer;
    box-sizing: border-box;
  }
`;

const ChatItemTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  width: 100%;
`;

const StepsTrackerContainer = styled.div`
  padding-left: 24px;
`;

const SuggestedActions = styled(NewButton)`
  display: flex;
  row-gap: 4px;
  border: 1px solid var(--border-default);
  margin: 12px 0;
  margin-bottom: 20px;
  box-shadow: 0px 2px 5px -1px rgba(50, 50, 93, 0.1), 0px 1px 3px -1px rgba(0, 0, 0, 0.1);
`;

const ChatItem = ({
  question,
  users,
  switchToChartMode,
  handleCreateQuestionFromPrompt,
  handleViewChange,
  setActiveChildQuestion,
  activeChildQuestion,
  sources = null,
  isLastQuestion = false,
  onQuestionSelect = null,
  onUndoClick = null,
  onRetryQuestion = null,
  isSelected = false,
  dataDescription = null,
  setBranchQuestion = null,
  isReadOnly = false,
  isSaved = false,
  isSqlChanged = false,
  isSavedAssetQuestion = false,
  currentOrganizationName = null,
  branchQuestion = null,
  onOpenDataDictionaryModal = null,
  isAgenticChat = false
}) => {
  const { showPortal } = usePortal();
  const navigate = useNavigate();

  const steps =
    question?.childQuestions?.map((q) => ({
      body: q.body,
      isCanned: q?.answer?.isCanned,
      id: q.id
    })) || [];

  const handleOnQuestionSelect = useCallback(
    (view = 'table') => {
      const selection = window.getSelection();
      if (selection && selection.toString()) {
        return;
      }
      onQuestionSelect?.(view);
    },
    [onQuestionSelect]
  );

  const filteredSources = useMemo(
    () =>
      sources?.filter((source) => {
        if (question?.answer?.schemas) {
          if (
            question.answer.schemas
              .filter((s) => s !== 'multi' && s !== 'metrics')
              .includes(source.integration.schema)
          ) {
            return true;
          }
        }
        return false;
      }),
    [question, sources]
  );

  const onCreateNewBranch = () => {
    handleOnQuestionSelect();
    setBranchQuestion(question);
  };

  const handleSwitchToChartMode = () => {
    switchToChartMode();
    handleOnQuestionSelect('chart');
  };

  // add handlers here
  const handleOpenSourceTab = useCallback(() => {
    handleViewChange('source');
  }, [handleViewChange]);

  const handleOpenDataDictionary = useCallback(() => {
    onOpenDataDictionaryModal?.();
  }, [onOpenDataDictionaryModal]);

  const handleAddChart = useCallback(() => {
    handleViewChange('chart');
    handleCreateQuestionFromPrompt(question.body, true);
  }, [handleCreateQuestionFromPrompt, handleViewChange, question.body]);

  const handleRetry = useCallback(
    (e) => {
      onRetryQuestion?.(e);
    },
    [onRetryQuestion]
  );

  const handleRemoveQuestion = useCallback(
    (e) => {
      onUndoClick(e);
    },
    [onUndoClick]
  );

  const handleHelpClick = useCallback(() => {
    showPortal(<NuxModal onComplete={() => navigate('/chats/new')} />);
  }, [showPortal]);

  const renderAsset = useCallback(() => {
    // first check if the question is a category question
    if (question.isCategoryQuestion) {
      return (
        <ChatCannedAnswer
          category={question?.category?.[0]}
          handleOpenSourceTab={handleOpenSourceTab}
          handleOpenDataDictionary={handleOpenDataDictionary}
          handleAddChart={handleAddChart}
          handleRetry={handleRetry}
          handleRemoveQuestion={handleRemoveQuestion}
          handleHelpClick={handleHelpClick}
        />
      );
    }
    // next check if it's a canned answer
    if (question?.answer?.isCanned) {
      return (
        <>
          <SuggestedActions onClick={() => handleOnQuestionSelect()}>
            <Icon name='help' displaySize={20} />
            <Text $bold>Suggested Actions</Text>
          </SuggestedActions>
          {question.childQuestions.length > 0 && (
            <ChildQuestionsToggle>
              {/* steps.length + 1 to mark q as complete */}
              <StepsTracker
                steps={steps}
                currentStep={steps.length + 1}
                setActiveChildQuestion={setActiveChildQuestion}
                activeChildQuestion={activeChildQuestion}
              />
            </ChildQuestionsToggle>
          )}
        </>
      );
    }
    // finally check if it's a regular question
    if (
      question?.answer &&
      question?.status === null &&
      question.answer.generatedBy !== 'ClarifyQuestionProcessor'
    ) {
      // For agentic responses, don't render ChatAsset - just show the reply text in the Message component
      if (isAgenticChat) {
        return null;
      }

      // For regular chat, render ChatAsset as normal
      const title = question?.answer?.vegaSpec?.title?.text || question?.answer?.vegaSpec?.title;
      return (
        <ChatAsset
          onQuestionSelect={handleOnQuestionSelect}
          dataDescription={dataDescription}
          title={title || question.assetTitle || 'Answer'}
          isSelected={isSelected}
          sources={filteredSources}
          isCurrentBranchQuestion={question.id === branchQuestion?.id}
          isLastQuestion={isLastQuestion}
          isReadOnly={isReadOnly}
          isSuccessfulQuestion={Boolean(question?.answer)}
          onUndoClick={onUndoClick}
          onRetryQuestion={onRetryQuestion}
          onCreateNewBranch={onCreateNewBranch}
          isSaved={isSaved}
          hasChart={Boolean(question.lastChartFollowUp)}
          onSwitchToChartMode={handleSwitchToChartMode}
          isSqlChanged={isSqlChanged}
          isSavedAssetQuestion={isSavedAssetQuestion}
          currentOrganizationName={currentOrganizationName}
          contextQuestions={question?.contextQuestions}
          isChartFollowUp={question?.isChart}
          steps={steps}
          setActiveChildQuestion={setActiveChildQuestion}
          activeChildQuestion={activeChildQuestion}
        />
      );
    }
    return null;
  }, [
    question,
    handleOnQuestionSelect,
    dataDescription,
    isSelected,
    filteredSources,
    branchQuestion,
    isLastQuestion,
    isReadOnly,
    onUndoClick,
    onRetryQuestion,
    onCreateNewBranch,
    isSaved,
    isSqlChanged,
    isSavedAssetQuestion,
    currentOrganizationName
  ]);

  const messageCreator = useMemo(() => {
    const creator = users?.find((user) => user.id === question.userId);
    return creator;
  }, [users, question]);

  const questionAnswer = useMemo(() => {
    if (question?.isChart) {
      if (question?.answer?.vegaSpec) {
        const title = question?.answer?.vegaSpec?.title?.text || question?.answer?.vegaSpec?.title;
        return title
          ? `Created a chart titled ${title}. Is there anything else you want to change?`
          : `Created a chart. Is there anything else you want to change?`;
      }
      return question?.statusMessage;
    }

    if (isSavedAssetQuestion) {
      return `**Started chat on** ${question?.assetTitle || question?.answer?.body}`;
    }

    if (question.isClarify) {
      return question?.answer?.body || question?.statusMessage;
    }

    // For agentic chat, show the full answer without extraction
    if (isAgenticChat && question?.answer && question?.status === null) {
      return question?.assetTitle || question?.answer?.body || question?.statusMessage || '';
    }

    return question?.assetTitle || question?.answer?.body || question?.statusMessage;
  }, [question, isSavedAssetQuestion, isAgenticChat]);

  const renderedAsset = renderAsset();

  return (
    <ChatItemContainer $opacity={branchQuestion && branchQuestion.id !== question.id}>
      <ChatItemInnerContainer>
        <ChatItemTextContainer>
          {!isSavedAssetQuestion && (
            <Message text={question.body} avatarURL={messageCreator?.picture} />
          )}
          <Message
            text={questionAnswer}
            asset={renderedAsset}
            isLastClarifyQuestion={question.isClarify && isLastQuestion}
            isAgenticChat={isAgenticChat}
            onUndoClick={(evt) => onUndoClick(evt, question.id)}
          />
          <StepsTrackerContainer>
            {question.childQuestions.length > 0 && !question?.answer && (
              <StepsTracker steps={steps} currentStep={steps.length - 1} />
            )}
          </StepsTrackerContainer>
        </ChatItemTextContainer>
      </ChatItemInnerContainer>
    </ChatItemContainer>
  );
};

ChatItem.propTypes = {
  users: PropTypes.arrayOf(userShape),
  question: questionShape.isRequired,
  switchToChartMode: PropTypes.func,
  isSelected: PropTypes.bool,
  sources: PropTypes.arrayOf(sourceShape),
  isLastQuestion: PropTypes.bool,
  onQuestionSelect: PropTypes.func,
  onUndoClick: PropTypes.func,
  onRetryQuestion: PropTypes.func,
  dataDescription: PropTypes.string,
  setBranchQuestion: PropTypes.func,
  isReadOnly: PropTypes.bool,
  isSaved: PropTypes.bool,
  isSqlChanged: PropTypes.bool,
  isSavedAssetQuestion: PropTypes.bool,
  currentOrganizationName: PropTypes.string,
  branchQuestion: questionShape,
  onOpenDataDictionaryModal: PropTypes.func,
  handleCreateQuestionFromPrompt: PropTypes.func,
  handleViewChange: PropTypes.func,
  setActiveChildQuestion: PropTypes.func,
  activeChildQuestion: PropTypes.number,
  isAgenticChat: PropTypes.bool
};

export { ChatItem };
